const WEB3FORMS_KEY = "420865fc-9ca8-45bc-8313-8f3a8dea4978";
const HCAPTCHA_SITE_KEY = "50b2fe65-b00b-4b9e-ad62-3ba471098be2";
const HCAPTCHA_CALLBACK = "__byggroHcaptchaReady";
const HCAPTCHA_SRC = `https://js.hcaptcha.com/1/api.js?render=explicit&onload=${HCAPTCHA_CALLBACK}`;
const MIN_FILL_MS = 3000;
const LIMITS = { namn: 80, foretag: 120, epost: 254 };
const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const CONTACT_FALLBACK = "byggro@northcollective.se";

let hcaptchaPromise = null;

const loadHcaptcha = () => {
  if (hcaptchaPromise) return hcaptchaPromise;
  hcaptchaPromise = new Promise((resolve, reject) => {
    if (window.hcaptcha) {
      resolve(window.hcaptcha);
      return;
    }
    window[HCAPTCHA_CALLBACK] = () => resolve(window.hcaptcha);
    const script = document.createElement("script");
    script.src = HCAPTCHA_SRC;
    script.async = true;
    script.defer = true;
    script.onerror = () => reject(new Error("hcaptcha unreachable"));
    document.head.appendChild(script);
  });
  return hcaptchaPromise;
};

const CTA = () => {
  const [submitted, setSubmitted] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [form, setForm] = React.useState({ namn: "", foretag: "", anstallda: "1–3", epost: "" });

  const captchaRef = React.useRef(null);
  const widgetId = React.useRef(null);
  const botcheck = React.useRef(null);
  const openedAt = React.useRef(Date.now());

  React.useEffect(() => {
    let cancelled = false;
    loadHcaptcha()
      .then((hcaptcha) => {
        if (cancelled || !captchaRef.current || widgetId.current !== null) return;
        widgetId.current = hcaptcha.render(captchaRef.current, { sitekey: HCAPTCHA_SITE_KEY });
      })
      .catch(() => {
        if (!cancelled) setError("Kunde inte ladda robotkontrollen. Ladda om sidan eller maila " + CONTACT_FALLBACK + ".");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const resetCaptcha = () => {
    if (widgetId.current !== null && window.hcaptcha) window.hcaptcha.reset(widgetId.current);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null);

    if (botcheck.current?.checked) return;

    if (Date.now() - openedAt.current < MIN_FILL_MS) {
      setError("Vänta ett ögonblick innan du skickar.");
      return;
    }

    const namn = form.namn.trim();
    const foretag = form.foretag.trim();
    const epost = form.epost.trim();

    if (!namn || !foretag || !EMAIL_PATTERN.test(epost)) {
      setError("Kontrollera att namn, företag och e-postadress är korrekt ifyllda.");
      return;
    }

    const captcha = widgetId.current !== null && window.hcaptcha ? window.hcaptcha.getResponse(widgetId.current) : "";
    if (!captcha) {
      setError("Bekräfta att du inte är en robot.");
      return;
    }

    setSending(true);
    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          access_key: WEB3FORMS_KEY,
          subject: `Demo-bokning från ${foretag || namn}`,
          from_name: "Byggro demo-formulär",
          namn,
          foretag,
          anstallda: form.anstallda,
          epost,
          botcheck: "",
          "h-captcha-response": captcha,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setSubmitted(true);
      } else {
        resetCaptcha();
        setError(data.message || `Något gick fel. Försök igen eller maila ${CONTACT_FALLBACK}.`);
      }
    } catch (err) {
      resetCaptcha();
      setError(`Kunde inte skicka. Kolla din anslutning eller maila ${CONTACT_FALLBACK}.`);
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="section cta-section" id="demo">
      <div className="container">
        <div className="cta-grid">
          <div>
            <h2 style={{ maxWidth: 480 }}>30 minuter. Vi visar Byggro mot din verklighet.</h2>
            <p style={{ marginTop: 18, fontSize: 15.5, maxWidth: 440 }}>
              Berätta om din firma så förbereder vi en genomgång baserad på hur du jobbar idag — med dina typer av jobb, offerter och projekt.
            </p>
            <ul className="cta-list">
              <li>
                <span className="check">✓</span> Anpassad genomgång utifrån din verksamhet
              </li>
              <li>
                <span className="check">✓</span> Se affärer, offerter och projektstyrning live
              </li>
              <li>
                <span className="check">✓</span> Inga säljare — du pratar med oss direkt
              </li>
            </ul>
          </div>
          <div className="cta-form card">
            {submitted ? (
              <div className="form-success" style={{ textAlign: "center" }}>
                <div className="check-big" style={{ margin: "0 auto" }}>
                  ✓
                </div>
                <h3 style={{ marginTop: 20 }}>Tack {form.namn || ""} — vi hör av oss inom kort.</h3>
                <p style={{ marginTop: 10, fontSize: 14 }}>
                  Har du en offert eller projekt du vill att vi förbereder mot? Förbered gärna ett sådant för en bättre demo.
                </p>
              </div>
            ) : (
              <form onSubmit={submit}>
                <div className="form-head">
                  <span style={{ fontWeight: 600 }}>Boka demo</span>
                  <span className="mono" style={{ fontSize: 11.5, color: "var(--ink-4)" }}>
                    ~30 min · video eller på plats
                  </span>
                </div>

                <div style={{ position: "absolute", left: "-9999px", width: 1, height: 1, overflow: "hidden" }} aria-hidden="true">
                  <label htmlFor="botcheck">Lämna detta fält tomt</label>
                  <input id="botcheck" name="botcheck" type="checkbox" ref={botcheck} tabIndex={-1} autoComplete="off" />
                </div>

                <Field label="Namn" v={form.namn} onChange={(v) => setForm({ ...form, namn: v })} placeholder="Erik Johansson" maxLength={LIMITS.namn} />
                <Field label="Företag" v={form.foretag} onChange={(v) => setForm({ ...form, foretag: v })} placeholder="Johansson Bygg AB" maxLength={LIMITS.foretag} />
                <div className="field">
                  <label>Antal anställda</label>
                  <div className="seg">
                    {["1–3", "4–10", "11–25", "25+"].map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        className={`seg-opt ${form.anstallda === opt ? "active" : ""}`}
                        onClick={() => setForm({ ...form, anstallda: opt })}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
                <Field label="E-post" v={form.epost} onChange={(v) => setForm({ ...form, epost: v })} placeholder="erik@johanssonbygg.se" type="email" maxLength={LIMITS.epost} />

                <div ref={captchaRef} style={{ marginTop: 14 }} />

                <button
                  type="submit"
                  disabled={sending}
                  aria-busy={sending}
                  className="btn btn-primary"
                  style={{ width: "100%", justifyContent: "center", marginTop: 8, opacity: sending ? 0.6 : 1 }}
                >
                  {sending ? (
                    "Skickar…"
                  ) : (
                    <>
                      Skicka & boka tid
                      <span className="arr" />
                    </>
                  )}
                </button>
                {error && <div role="alert" style={{ fontSize: 12.5, color: "var(--primary)", marginTop: 12, textAlign: "center" }}>{error}</div>}
                <div style={{ fontSize: 11.5, color: "var(--ink-4)", marginTop: 14, textAlign: "center", fontFamily: "var(--font-mono)" }}>
                  Inga säljare. Ingen autosvarare. Bara tider.
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, v, onChange, placeholder, type = "text", maxLength }) => (
  <div className="field">
    <label>{label}</label>
    <input type={type} className="input" value={v} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} maxLength={maxLength} required />
  </div>
);

Object.assign(window, { CTA });
